import { Link, useRouterState } from "@tanstack/react-router";
import { MenuIcon } from "lucide-react";
import { type ReactNode, useState } from "react";
import { LocaleSwitcher, useT } from "@/i18n";
import { AccountLink } from "../AccountLink";
import { Button } from "../ui/button";
import type { NavigationLink } from "./navigation-dialog";
import { navigationDialog, useWarmNavigationDialog } from "./navigation-dialog-loader";
import { ThemeSwitcher } from "./theme-switcher";

/** One global navigation contract, independent of tournament and credential.
 * The same outer width on documents and workspaces keeps its controls in place;
 * reading widths and local sections belong to the content below. */
export function AppHeader({
  pageLinks = [],
  children,
}: {
  pageLinks?: NavigationLink[];
  /** Existing scoped access/notification controls; never derive permissions here. */
  children?: ReactNode;
}) {
  const t = useT();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const [open, setOpen] = useState(false);
  // Loaded ahead on a phone, and rendered the moment it is in hand: see
  // `navigation-dialog-loader.ts` for the 300 ms the old Suspense cost.
  const NavigationDialog = navigationDialog.useComponent();
  useWarmNavigationDialog();
  const links = [
    {
      href: "/",
      label: t("nav.tournaments"),
      current: pathname === "/" || /^\/t(?:\/|$)/.test(pathname),
    },
    { href: "/teams", label: t("directory.title"), current: /^\/teams?(?:\/|$)/.test(pathname) },
    { href: "/players", label: t("players.title"), current: /^\/players(?:\/|$)/.test(pathname) },
    { href: "/rules", label: t("board.rulesLink"), current: pathname === "/rules" },
  ];
  return (
    <>
      <header className="sticky top-0 z-30 px-3 pt-3 adm:px-4">
        <div className="glass board-float mx-auto flex w-full max-w-3xl items-center gap-2 px-3 py-3 text-foreground adm:max-w-6xl adm:gap-4 adm:px-4">
          <Link
            to="/"
            aria-label={t("board.allTournaments")}
            className="score-wordmark mr-auto shrink-0 py-2 text-xl leading-none text-brand-ink transition-opacity hover:opacity-80 adm:text-2xl dark:text-brand"
          >
            SCORE
          </Link>
          <nav className="hidden items-center gap-1 adm:flex" aria-label={t("nav.browse")}>
            {links.map((link) => (
              <Button
                key={link.href}
                variant="ghost"
                aria-current={link.current ? "page" : undefined}
                className={link.current ? "bg-accent text-foreground" : "text-muted-foreground"}
                render={<Link to={link.href} />}
              >
                {link.label}
              </Button>
            ))}
          </nav>
          <AccountLink />
          <LocaleSwitcher className="hidden shrink-0 adm:inline-flex" />
          <ThemeSwitcher compact className="hidden adm:inline-flex" />
          <Button
            variant="outline"
            className="adm:hidden"
            aria-expanded={open}
            aria-haspopup="dialog"
            // A finger lands ~100 ms before the click: start the download
            // then, in case the idle warm-up has not happened yet. The sheet
            // opens once it is in hand, never half-open on a failed fetch.
            onPointerDown={() => void navigationDialog.load()}
            onClick={() => {
              if (NavigationDialog !== null) setOpen(true);
              else void navigationDialog.load().then((ok) => ok && setOpen(true));
            }}
          >
            <MenuIcon aria-hidden="true" className="hidden sm:block" />
            {t("board.menu")}
          </Button>
        </div>
      </header>
      {/* A portal must never belong to a display:none breakpoint branch. */}
      {open && NavigationDialog !== null && (
        <NavigationDialog
          title={t("board.menu")}
          links={links}
          pageLinks={pageLinks}
          global
          onClose={() => setOpen(false)}
        >
          {children}
        </NavigationDialog>
      )}
    </>
  );
}
